import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  expireSession,
  selectSessionAuthState,
} from "../store/reducers/sessionSlice";

/** Largest delay setTimeout honours (2^31 - 1 ms, about 24.8 days). */
export const MAX_TIMEOUT_MS = 2_147_483_647;

export const useSessionExpiration = () => {
  const dispatch = useDispatch();
  const { expiresAt, sessionStatus } = useSelector(selectSessionAuthState);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (sessionStatus !== "VALID_SESSION" || !expiresAt) {
      return;
    }

    const expirationTime = new Date(expiresAt).getTime();

    // MED-1622: sessions now last 90 days, beyond setTimeout's 24.8-day
    // limit — a longer delay fires at once and would log out every user.
    // Wait in steps of at most that limit instead.
    const schedule = () => {
      const timeUntilExpiration = expirationTime - Date.now();
      if (timeUntilExpiration <= 0) {
        dispatch(expireSession());
        return;
      }
      timeoutRef.current = setTimeout(
        schedule,
        Math.min(timeUntilExpiration, MAX_TIMEOUT_MS)
      );
    };
    schedule();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [expiresAt, sessionStatus, dispatch]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
};
